import React, {FC, useEffect, useRef, useState} from 'react';

import {globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {Banner, BannerTypesEnum} from '../../common/banners/banner';
import {BannerIcon} from '../../common/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../common/banners/bannersContext';
import {BannerText} from '../../common/banners/bannerText';

/*
 * Props.
 */

interface CalendarVersionImportBannerProps {
  /** A boolean indicating if there are any calendar event import jobs running for selected calendar versions. */
  isImporting: boolean;
}

/*
 * Component.
 */

export const CalendarVersionImportBanner: FC<CalendarVersionImportBannerProps> = (props) => {
  const {isImporting} = props;
  const previousIsImportingRef = useRef(isImporting);
  const hasIsImportingChanged = isImporting !== previousIsImportingRef.current;
  const [shouldDisplaySuccess, setShouldDisplaySuccess] = useState(false);

  useEffect(() => {
    // We're only interested in state transitions.
    if (isImporting === previousIsImportingRef.current) {
      return undefined;
    }

    previousIsImportingRef.current = isImporting;

    // If there are no more calendar event import jobs, display the success banner for 8s.
    if (!isImporting) {
      setShouldDisplaySuccess(true);
      const timeout = globalWindow.setInstrumentedTimeout(() => {
        setShouldDisplaySuccess(false);
      }, 8 * 1000);
      return () => globalWindow.clearTimeout(timeout);
    }

    // Otherwise, if a new import started, reset the state.
    setShouldDisplaySuccess(false);
    return undefined;
  }, [isImporting]);

  // If nothing is going on, no need to display a banner at all.
  if (!isImporting && !shouldDisplaySuccess && !hasIsImportingChanged) {
    return null;
  }

  // Otherwise, check if the import is still in progress or if it has just completed.
  return isImporting ? renderProgressBanner() : renderCompletedBanner();
};

function renderProgressBanner() {
  return (
    <Banner bannerType={BannerTypesEnum.INLINE} priority={BannerPrioritiesEnum.INFORMATION}>
      <BannerIcon iconName="repeatCircleSmall" shouldRotate />
      <BannerText>
        <Trans id="calendar-version-import-importing">Importing calendar events...</Trans>
      </BannerText>
    </Banner>
  );
}

function renderCompletedBanner() {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Banner
          bannerType={BannerTypesEnum.INLINE}
          priority={BannerPrioritiesEnum.INFORMATION}
          color={theme.palette.green.shade40}
        >
          <BannerIcon iconName="checkmarkCircleSmall" />
          <BannerText>
            <Trans id="calendar-version-import-complete">Calendar import complete</Trans>
          </BannerText>
        </Banner>
      )}
    </UserThemeConsumer>
  );
}
